// Defaults applied to the New Job form when a job ID is picked, plus the
// [placeholder] variables each job substitutes into its title and message.
// Titles and messages match the backend fallbacks, so leaving them unchanged
// behaves the same as leaving them empty. Status filters are deliberately not
// defaulted: a job-level statusTypeCode/eligibleStatusCodes overrides the
// study's activeStatusCodes.

const isDev = import.meta.env.DEV;
const cronByEnv = (devCron, prodCron) => (isDev ? devCron : prodCron);
const allDailySendHours = () => Array.from({ length: 24 }, (_, hour) => hour);

const formComplianceDefaults = {
    enabled: false,
    cronSchedule: cronByEnv("*/30 * * * *", "0 * * * *"),
    messageTitle: "Daily form reminder",
    message: "Please complete your [formName] form for [formDateDisplay].",
    recipientType: "participant",
    description: "Reminds participants who have not submitted the form set in formGUID within the last durationHours hours. Sends once a day at dailySendHourLocal (participant local time).",
    parameters: {
        formGUID: null,
        durationHours: 24,
        dailySendHourLocal: 8,
        targetSubjectId: null,
        sendEmailNotification: false
    }
};

const glucoseAlertParameters = {
    windowAnchor: "latestReading",
    consecutiveReadings: 3,
    maxReadingAgeMinutes: 60,
    realertAfterMinutes: 30,
    targetSubjectId: null,
    activityGracePeriodMinutes: 120,
    sendEmailNotification: false,
    hideReadingValueInEmail: true
};

const JOB_DEFAULTS_BY_ID = {
    "compliance-check": {
        enabled: false,
        cronSchedule: cronByEnv("*/30 * * * *", "0 * * * *"),
        messageTitle: "CGM Compliance Alert",
        message: "No CGM readings received in the last [nHours] hours",
        recipientType: "participant",
        description: "Alerts when a participant with a started CGM sensor has sent no CGM readings for nHours hours. Sends only at the local hours listed in dailySendHoursLocal; set ignoreDailyHour to true to send at any hour. realertAfterHours limits repeats while the data stays missing: empty alerts on every run, 0 alerts once, X alerts again at most every X hours.",
        parameters: {
            nHours: 1,
            realertAfterHours: null,
            dailySendHoursLocal: allDailySendHours(),
            targetSubjectId: "",
            sendEmailNotification: false
        }
    },
    "device-activity-check": {
        enabled: false,
        cronSchedule: cronByEnv("*/30 * * * *", "0 */6 * * *"),
        messageTitle: "Device Activity Reminder",
        message: "The CGM collection phase is underway, but no device data has been received. Please ensure your device is active and recording measurements.",
        recipientType: "participant",
        description: "Reminds the participant in targetSubjectId (required; the job checks only that one subject) when their milestone activity (milestoneActivityType) has started but no device activity (deviceActivityType) has.",
        parameters: {
            deviceActivityType: "MEAS",
            milestoneActivityType: "MILE",
            targetSubjectId: "",
            sendEmailNotification: false
        }
    },
    "generate-cgm-reading": {
        enabled: false,
        cronSchedule: "*/5 * * * *",
        messageTitle: "",
        message: "",
        recipientType: "participant",
        description: "Test-data generator: writes a simulated CGM reading for each subject in targetSubjectId (comma-separated) on every run. Sends no messages. Use on dev/test only.",
        parameters: {
            targetSubjectId: "",
            externalId: "422385272846-3262",
            timezone: "America/New_York",
            protocolNumber: "ST01",
            manufacturer: "Dexcom",
            model: "G7"
        }
    },
    "glucose-reading-compliance-check": {
        enabled: false,
        cronSchedule: cronByEnv("*/30 * * * *", "0 * * * *"),
        messageTitle: "Glucose reading reminder",
        message: "You have not recorded a [readingLabel] in the past [thresholdDays] days.",
        recipientType: "participant",
        description: "Reminds participants who have not recorded a reading of the types in readingTypes within thresholdDays days. With windowMode \"localDay\" only readings since the participant's local midnight count (thresholdDays is ignored). Sends once a day at dailySendHourLocal (participant local time).",
        parameters: {
            readingTypes: ["FINGERSTICK"],
            readingLabel: "finger stick",
            thresholdDays: 7,
            windowMode: "rolling",
            dailySendHourLocal: 8,
            targetSubjectId: "",
            sendEmailNotification: false
        }
    },
    "low-glucose-check": {
        enabled: false,
        cronSchedule: "*/5 * * * *",
        messageTitle: "Low Glucose Alert",
        message: "A glucose reading below [lowThreshold] mg/dL was detected.",
        recipientType: "site",
        description: "Alerts when the participant's last consecutiveReadings CGM readings are all below lowThreshold mg/dL (one missing reading is accepted). The window ends at the newest reading, so delayed sensor uploads are still caught; readings older than maxReadingAgeMinutes are ignored. While the episode lasts, the alert repeats at most every realertAfterMinutes. The participant needs a measurement activity started at least activityGracePeriodMinutes ago.",
        parameters: { lowThreshold: 70, ...glucoseAlertParameters }
    },
    "high-glucose-check": {
        enabled: false,
        cronSchedule: "*/5 * * * *",
        messageTitle: "High Glucose Alert",
        message: "A glucose reading above [highThreshold] mg/dL was detected.",
        recipientType: "site",
        description: "Alerts when the participant's last consecutiveReadings CGM readings are all above highThreshold mg/dL (one missing reading is accepted). The window ends at the newest reading, so delayed sensor uploads are still caught; readings older than maxReadingAgeMinutes are ignored. While the episode lasts, the alert repeats at most every realertAfterMinutes. The participant needs a measurement activity started at least activityGracePeriodMinutes ago.",
        parameters: { highThreshold: 250, ...glucoseAlertParameters }
    },
    "measurement-activity-auto-complete": {
        enabled: false,
        cronSchedule: cronByEnv("*/30 * * * *", "0 * * * *"),
        messageTitle: "Measurement activity completed",
        message: "Your [activityName] measurement activity was automatically completed after [nDays] days without further data collection.",
        recipientType: "both",
        description: "Completes started measurement activities (activityTypeCode) once they have been running for completionAfterDays days, and notifies the recipients.",
        parameters: {
            activityTypeCode: "MEAS",
            startedStatusCode: "START",
            completedStatusCode: "COMP",
            completionAfterDays: 14,
            targetSubjectId: "",
            sendEmailNotification: false
        }
    },
    "form-compliance-check": formComplianceDefaults,
    "form-compliance-check-insulin-daily": formComplianceDefaults,
    "form-compliance-check-insulin-weekly": formComplianceDefaults,
    "sensor-replacement-reminder": {
        enabled: false,
        cronSchedule: cronByEnv("*/30 * * * *", "0 * * * *"),
        messageTitle: "Sensor replacement reminder",
        message: "Your measurement sensor was started on [sensorStartedDisplay]. Please replace it today ([replaceByDateDisplay]). This is day [remindAfterDays] after start.",
        recipientType: "participant",
        description: "Reminds participants to replace their sensor remindAfterDays days after it was started. deviceCode is required. Sends at dailySendHourLocal (participant local time).",
        parameters: {
            deviceActivityType: "MEAS",
            deviceCode: "",
            remindAfterDays: 14,
            dailySendHourLocal: 8,
            targetSubjectId: "",
            sendEmailNotification: false
        }
    },
    "subject-activity-cgm-compliance": {
        enabled: false,
        cronSchedule: "0 * * * *",
        messageTitle: "CGM Reminder",
        message: "",
        recipientType: "participant",
        description: "Visit-related CGM compliance rules for participants. Message texts come from the localization entries subject-activity-cgm-compliance-rule1 to -rule5, not from the title and message fields.",
        parameters: {
            dailySendHourLocal: 8,
            activityTypeCode: "MILE",
            startedStatusCode: "START",
            targetSubjectId: "",
            sendEmailNotification: false,
            rule2WeekOffsetDays: 7,
            rule3MinSufficientDays: 10,
            rule4MinSufficientDays: 20,
            rule4StartDaysBeforePlanned: 6,
            expectedReadingsPerDay: 288,
            sufficientReadingFraction: 0.8,
            sufficientDayMaxGapHours: 2,
            sensorLifetimeReminderDays: 10,
            allowDevRuleTestOverrides: false
        }
    },
    "subject-activity-cgm-site-compliance": {
        enabled: false,
        cronSchedule: "0 * * * *",
        messageTitle: "CGM Site Compliance Alert",
        message: "",
        recipientType: "site",
        description: "Visit-related CGM compliance alerts for site staff. The title is fixed and the message texts are built in per rule; the title and message fields are not used.",
        parameters: {
            dailySendHourLocal: 8,
            activityTypeCode: "MILE",
            startedStatusCode: "START",
            targetSubjectId: "",
            sendEmailNotification: true,
            rule2WeekOffsetDays: 7,
            minimumUsableDaysForAlert: 10,
            requiredAnyDataDays: 20,
            expectedReadingsPerDay: 288,
            sufficientReadingFraction: 0.8,
            sufficientDayMaxGapHours: 2
        }
    }
};

const formPlaceholders = ["formName", "formDate", "formDateDisplay", "formLastFound"];

const TEMPLATE_VARIABLES_BY_ID = {
    "compliance-check": ["nHours"],
    "glucose-reading-compliance-check": ["readingLabel", "readingTypes", "thresholdDays", "lastReadingDisplay"],
    "low-glucose-check": ["lowThreshold", "value", "subjectId"],
    "high-glucose-check": ["highThreshold", "value", "subjectId"],
    "measurement-activity-auto-complete": ["activityName", "nDays", "activityStarted", "completedAt"],
    "form-compliance-check": formPlaceholders,
    "form-compliance-check-insulin-daily": formPlaceholders,
    "form-compliance-check-insulin-weekly": formPlaceholders,
    "sensor-replacement-reminder": ["sensorStartedDisplay", "replaceByDateDisplay", "remindAfterDays"]
};

export function createBaseJobData() {
    return {
        jobId: "",
        variantName: "default",
        enabled: true,
        cronSchedule: "",
        parameters: "{}",
        description: "",
        messageTitle: "",
        message: "",
        recipientType: "participant"
    };
}

/** Fresh form data for a new job of `jobId`; parameters are pretty-printed JSON. */
export function buildJobDataForJobId(jobId) {
    const defaults = JOB_DEFAULTS_BY_ID[jobId];
    return {
        ...createBaseJobData(),
        ...defaults,
        jobId: jobId ?? "",
        parameters: JSON.stringify(defaults?.parameters ?? {}, null, 2)
    };
}

const RECIPIENT_HINTS = {
    participant: "Sent to the participant.",
    site: "Sent to the site staff.",
    both: "Sent to participant and site."
};

export function recipientTypeHint(recipientType) {
    return RECIPIENT_HINTS[recipientType] ?? "Who receives the message.";
}

export function templateVariablesHint(jobId) {
    if (!jobId) return "Select a job ID to see available placeholders.";
    const variables = TEMPLATE_VARIABLES_BY_ID[jobId] ?? [];
    if (!variables.length) return "No placeholders available for this job.";
    return `Available placeholders: ${variables.map((v) => `[${v}]`).join(", ")}`;
}
